import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import { zetWerkgebied } from "@/lib/vakman.functions";
import { VakmanPoort, Veld, invoer, useVakmanProfiel, useSessie } from "@/components/vakman";

export const Route = createFileRoute("/vakman/instellingen")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Werkgebied instellen | Vakmanportaal" },
      { name: "description", content: "Stel in binnen hoeveel kilometer vanaf je adres je aanvragen wilt zien." },
      { property: "og:title", content: "Werkgebied instellen | Vakmanportaal" },
      { property: "og:description", content: "Stel je werkgebied in." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => <VakmanPoort>{() => <Instellingen />}</VakmanPoort>,
});

function Instellingen() {
  const [ingelogd] = useSessie();
  const profiel = useVakmanProfiel(ingelogd === true);
  const zet = useServerFn(zetWerkgebied);
  const qc = useQueryClient();
  const [plaats, setPlaats] = useState("");
  const [straal, setStraal] = useState(25);
  const [fout, setFout] = useState("");
  const [opgeslagen, setOpgeslagen] = useState(false);
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    if (profiel.data) {
      setPlaats(profiel.data.plaats);
      setStraal(profiel.data.straal_km);
    }
  }, [profiel.data]);

  async function opslaan(e: React.FormEvent) {
    e.preventDefault();
    setBezig(true);
    setFout("");
    setOpgeslagen(false);
    const res = await zet({ data: { plaats: plaats.trim(), straal_km: straal } });
    setBezig(false);
    if (!res.ok) return setFout(res.fout);
    setOpgeslagen(true);
    await qc.invalidateQueries({ queryKey: ["vakman-profiel"] });
    await qc.invalidateQueries({ queryKey: ["vakman-aanvragen"] });
  }

  return (
    <div className="rounded-xl border border-border bg-background p-6">
      <h1 className="text-2xl font-bold">Werkgebied</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Je ziet alleen aanvragen binnen deze afstand vanaf je adres. Pas je plaats of straal aan en je lijst wordt direct bijgewerkt.
      </p>
      <form onSubmit={(e) => void opslaan(e)} className="mt-6 space-y-5">
        <Veld label="Plaats van je bedrijf">
          <input required minLength={2} value={plaats} onChange={(e) => setPlaats(e.target.value)} className={invoer} />
        </Veld>
        <Veld label={`Straal: ${straal} km`}>
          <input
            type="range" min={5} max={150} step={5} value={straal}
            onChange={(e) => setStraal(Number(e.target.value))}
            className="mt-3 w-full accent-primary"
            aria-label="Straal in kilometers"
          />
          <div className="mt-1 flex justify-between text-xs text-muted-foreground"><span>5 km</span><span>150 km</span></div>
        </Veld>
        <div className="flex items-center gap-2 rounded-md bg-secondary px-4 py-3 text-sm">
          <MapPin className="h-4 w-4 shrink-0" />
          <span>Aanvragen binnen <strong>{straal} km</strong> rond <strong>{plaats || "…"}</strong></span>
        </div>
        {fout && <p className="text-sm font-medium text-destructive">{fout}</p>}
        {opgeslagen && <p className="text-sm font-medium text-primary">Opgeslagen. Je aanvragenlijst is bijgewerkt.</p>}
        <div className="flex items-center gap-4">
          <button disabled={bezig} className="rounded-md bg-primary px-6 py-3 font-bold text-primary-foreground disabled:opacity-40">
            {bezig ? "Opslaan…" : "Opslaan"}
          </button>
          <Link to="/vakman" className="text-sm text-muted-foreground underline">Terug naar aanvragen</Link>
        </div>
      </form>
    </div>
  );
}
